import { pageForKnowledgeId } from './data';
import { ArrowUpRightIcon } from './icons';
import { Link } from './router';
import type { SiteLanguage } from './types';
import './journey-selector.css';

type Language = 'en' | 'es';
type StartingPoint = 'explore' | 'planning' | 'support';

const guideIds: Record<StartingPoint, readonly string[]> = {
  explore: ['start-here'],
  planning: ['plan-your-surgery', 'recovery-essentials'],
  support: ['caregivers'],
};

const copy: Record<Language, {
  eyebrow: string;
  heading: string;
  intro: string;
  points: Record<StartingPoint, { title: string; body: string }>;
}> = {
  en: {
    eyebrow: 'Where to begin',
    heading: 'Start wherever you are.',
    intro: 'A few clear places to begin. Pick what helps today, or keep exploring.',
    points: {
      explore: {
        title: 'Still figuring things out?',
        body: 'You can learn what the options mean before you know what you want.',
      },
      planning: {
        title: 'Planning or recovering?',
        body: 'Take one question at a time: preparation, support, and what recovery may involve.',
      },
      support: {
        title: 'Here for someone else?',
        body: 'Find practical ways to help while leaving decisions with the person having surgery.',
      },
    },
  },
  es: {
    eyebrow: 'Por dónde empezar',
    heading: 'Empieza donde estés.',
    intro: 'Tres puntos de partida claros. Elige el que te sirva o sigue explorando.',
    points: {
      explore: {
        title: '¿Todavía estás explorando?',
        body: 'Puedes entender las opciones antes de saber qué quieres.',
      },
      planning: {
        title: '¿Te preparas o te estás recuperando?',
        body: 'Ve pregunta por pregunta: preparación, apoyo y recuperación.',
      },
      support: {
        title: '¿Acompañas a alguien?',
        body: 'Encuentra maneras prácticas de ayudar sin decidir por esa persona.',
      },
    },
  },
};

export function JourneySelector({ language }: { language: SiteLanguage }) {
  const lang: Language = language === 'es' ? 'es' : 'en';
  const c = copy[lang];

  return (
    <section className="starting-points shell" aria-labelledby="journey-selector-title">
      <header className="starting-points__heading">
        <span>{c.eyebrow}</span>
        <div>
          <h2 id="journey-selector-title">{c.heading}</h2>
          <p>{c.intro}</p>
        </div>
      </header>
      <div className="starting-points__list">
        {(Object.keys(guideIds) as StartingPoint[]).map((point) => (
          <article className="starting-points__item" key={point}>
            <div>
              <h3>{c.points[point].title}</h3>
              <p>{c.points[point].body}</p>
            </div>
            <div className="starting-points__links">
              {guideIds[point].map((id) => {
                const page = pageForKnowledgeId(lang, id);
                return page && page.wordCount > 0 ? (
                  <Link href={page.route} key={id}>{page.title}<ArrowUpRightIcon /></Link>
                ) : null;
              })}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
